import { createContext, useState, useEffect, useCallback } from 'react';
import { COMPANY_SETTINGS } from '../utils/constants';
import { usePresence } from '../hooks/usePresence';
import { useCommissions } from '../hooks/useCommissions';
import { useRestaurants } from '../hooks/useRestaurants';
import { loadUsers } from '../services/userService';

/**
 * App Context - Global State Management
 * Provides centralized state for the entire application
 */
export const AppContext = createContext(null);

export function AppProvider({ children }) {
  // Core State
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [settings, setSettings] = useState(COMPANY_SETTINGS);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Toast helper
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Custom Hooks
  usePresence(currentUser);
  
  const commissionHooks = useCommissions(currentUser, showToast);
  const restaurantHooks = useRestaurants(currentUser, showToast);

  // Restore session from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('gosnel_current_user');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        setCurrentUser(user);
      } catch (err) {
        console.error('Error restoring session:', err);
        localStorage.removeItem('gosnel_current_user');
      }
    }
  }, []);

  // Save user to localStorage when it changes
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('gosnel_current_user', JSON.stringify(currentUser));
    } else {
      localStorage.removeItem('gosnel_current_user');
    }
  }, [currentUser]);

  // Load users when admin/sales_manager logs in
  useEffect(() => {
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'sales_manager')) {
      loadUsersData();
    }
  }, [currentUser]);

  /**
   * Load all users from database
   */
  const loadUsersData = async () => {
    try {
      const usersData = await loadUsers();
      setUsers(usersData);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  /**
   * Login handler
   */
  const handleLogin = (user) => {
    setCurrentUser(user);
    // Navigation to role-based URL is handled by LoginPage
  };

  /**
   * Logout handler
   */
  const handleLogout = () => {
    setCurrentUser(null);
    setModal(null);
    setSelectedIds([]);
    localStorage.removeItem('gosnel_current_user');
    // Navigation to /login is handled by AuthenticatedLayout
  };

  /**
   * Open modal
   * Supports both patterns:
   * - openModal('add_user', data) 
   * - openModal({ type: 'add_user', data: ... })
   */
  const openModal = (typeOrObj, data = null) => {
    if (typeof typeOrObj === 'string') {
      setModal({ type: typeOrObj, data });
    } else {
      // Already an object with type property
      setModal(typeOrObj);
    }
  };

  /**
   * Close modal
   */
  const closeModal = () => {
    setModal(null);
  };

  // Context value
  const contextValue = {
    // State
    users,
    setUsers,
    currentUser,
    setCurrentUser,
    settings,
    setSettings,
    modal,
    setModal: openModal,
    closeModal,
    toast,
    showToast,
    selectedIds,
    setSelectedIds,
    
    // Hooks
    ...commissionHooks,
    ...restaurantHooks,
    
    // Actions
    loadUsers: loadUsersData,
    login: handleLogin,
    logout: handleLogout,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}
